import React, { useState, useRef, useEffect } from 'react';
import { Package, X, Save, ArrowRight, Trash2, Database, Printer, Warehouse, PackageX, MapPin, Tag } from 'lucide-react';
import { printingService, LabelType } from '../../../../services/printingService';
import { LocationSelectionModal } from '../../modules/warehouse-transfer/modals/LocationSelectionModal';
import { warehouseTransferService, ItemLocationInfo } from '../../../../services/warehouseTransferService';
import { stcBinService } from '../../../../services/stcBinService';
import { StcBinDto } from '../../../../types/stcBin';

interface ReceivingActiveLineProps {
  line: {
    itemNo: string;
    description1: string;
    quantity: number;           // Bestilt
    totalReceived: number;      // Mottatt
    arrivedQuantity: number;    // Ankommet
    remainingQuantity: number;  // Gjenstår
    itemUnitTxt: string;       // Enhet (stk, kg, etc)
    image?: string;            // Produktbilde URL
    itemType?: number;         // ItemType for batch-validering
  };
  // For editing existing StcTrans
  editingStcTrans?: {
    itemBatchNo: string;
    itemBatchExpDate: string;
    quantity: number;
    itemType?: number;
  } | null;
  onQuantityChange?: (newQuantity: number) => void;
  onBatchInfoChange?: (batchNo: string, expDate: string) => void;
  onSave?: (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => void;
  onCancel?: () => void;
  onDirectReceipt?: (quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => void;  // For direktemottak (både nye og eksisterende)
  onDelete?: () => void;          // For sletting (kun i edit-modus)
  currentQuantity?: number;
  showImage?: boolean;
  onOpenGrunndata?: () => void;   // For å åpne grunndata modal
  onPrintLabel?: (reportId: number | null, numLabels: number) => void;  // For å skrive ut etikett
  showPrintSection?: boolean;     // Vis etikett-utskrift seksjon
  companyNo?: string;             // CompanyNo for API calls
  warehouseID?: string;           // WarehouseID for receiving locations
  defaultReceivingLocation?: string;  // Default receiving location from profile (defaultGRBinCode)
  scannedBatchInfo?: {            // Batch-info fra skannet strekkode
    batchNo?: string;
    expDate?: string;
  };
  validationError?: string;       // Red error message for blocking validation
  validationWarning?: string;     // Yellow warning message for non-blocking validation
  enableSSCCPrinting?: boolean;   // Enable SSCC/STAND005 pallet label printing
  companyGS1Prefix?: string;      // Company GS1 prefix for SSCC generation (e.g., "703XXXXX")
  itemGTIN?: string;              // Item's GTIN for SSCC label
}

export const ReceivingActiveLine: React.FC<ReceivingActiveLineProps> = ({
  line,
  editingStcTrans,
  onQuantityChange,
  onBatchInfoChange,
  onSave,
  onCancel,
  onDirectReceipt,
  onDelete,
  currentQuantity = 0,
  showImage = false,
  onOpenGrunndata,
  onPrintLabel,
  showPrintSection = false,
  companyNo,
  warehouseID,
  defaultReceivingLocation,
  scannedBatchInfo,
  validationError,
  validationWarning,
  enableSSCCPrinting = false,
  companyGS1Prefix,
  itemGTIN
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [quantity, setQuantity] = useState(currentQuantity);
  const [batchNo, setBatchNo] = useState(editingStcTrans?.itemBatchNo || '');
  const [expDate, setExpDate] = useState(editingStcTrans?.itemBatchExpDate || '');
  const [inputJustFocused, setInputJustFocused] = useState(false);
  const [isQuantityTouched, setIsQuantityTouched] = useState(false); // Track if user has interacted with quantity
  const quantityInputRef = useRef<HTMLInputElement>(null);

  // Label printing state
  const [labelTypes, setLabelTypes] = useState<LabelType[]>([]);
  const [selectedLabelType, setSelectedLabelType] = useState<number | null>(null);
  const [numOfLabels, setNumOfLabels] = useState<number>(1);
  const [loadingLabelTypes, setLoadingLabelTypes] = useState(false);

  // Saldo modal state
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const [saldoLocations, setSaldoLocations] = useState<ItemLocationInfo[]>([]);
  const [loadingSaldoLocations, setLoadingSaldoLocations] = useState(false);

  // Receiving location state
  const [receivingLocations, setReceivingLocations] = useState<StcBinDto[]>([]);
  const [selectedReceivingLocation, setSelectedReceivingLocation] = useState<string>(defaultReceivingLocation || '');
  const [loadingReceivingLocations, setLoadingReceivingLocations] = useState(false);

  // SSCC label printing state
  const [ssccPrinting, setSSCCPrinting] = useState(false);
  const [ssccCounter, setSSCCCounter] = useState<number>(() => {
    // Initialize SSCC counter from localStorage for persistence across page reloads
    const stored = localStorage.getItem('ssccCounter');
    return stored ? parseInt(stored, 10) : 1;
  });

  const isDescriptionTruncated = line.description1 && line.description1.length > 60;
  const isEditingMode = !!editingStcTrans;

  // Update batch info and quantity when editingStcTrans changes
  useEffect(() => {
    if (editingStcTrans) {
      console.log('ReceivingActiveLine: editingStcTrans changed:', editingStcTrans);
      setBatchNo(editingStcTrans.itemBatchNo || '');
      setExpDate(editingStcTrans.itemBatchExpDate || '');
      setQuantity(editingStcTrans.quantity || 0);
    } else {
      // When not editing, check if we have scanned batch info
      if (scannedBatchInfo?.batchNo) {
        console.log('ReceivingActiveLine: Setting batch info from scanned barcode:', scannedBatchInfo);
        setBatchNo(scannedBatchInfo.batchNo);
      } else {
        setBatchNo('');
      }

      if (scannedBatchInfo?.expDate) {
        setExpDate(scannedBatchInfo.expDate);
      } else {
        setExpDate('');
      }
    }
  }, [editingStcTrans, scannedBatchInfo]);

  // Only update quantity from currentQuantity when NOT in editing mode
  useEffect(() => {
    if (!editingStcTrans) {
      setQuantity(currentQuantity);
      setIsQuantityTouched(false); // Reset touched state when new line is loaded
    }
  }, [currentQuantity, editingStcTrans]);

  // Load label types when showPrintSection is true
  useEffect(() => {
    if (showPrintSection && companyNo) {
      loadLabelTypes();
    }
  }, [showPrintSection, companyNo]);

  // Load receiving locations when component mounts
  useEffect(() => {
    if (companyNo && warehouseID) {
      loadReceivingLocations();
    }
  }, [companyNo, warehouseID]);

  // Update selected receiving location when default changes
  useEffect(() => {
    if (defaultReceivingLocation && !selectedReceivingLocation) {
      setSelectedReceivingLocation(defaultReceivingLocation);
    }
  }, [defaultReceivingLocation]);

  // Load item's default ReportID when line changes
  useEffect(() => {
    if (showPrintSection && companyNo && line.itemNo) {
      loadItemDefaultReport();
    }
  }, [showPrintSection, companyNo, line.itemNo]);

  // Auto-focus on quantity input when component mounts
  useEffect(() => {
    if (quantityInputRef.current) {
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  }, []);

  const loadLabelTypes = async () => {
    if (!companyNo) return;

    setLoadingLabelTypes(true);
    try {
      const types = await printingService.getLabelTypesForReceiving(companyNo);
      setLabelTypes(types);

      // Auto-select first label type if none selected
      if (types.length > 0 && !selectedLabelType) {
        setSelectedLabelType(types[0].reportID);
      }
    } catch (error) {
      console.error('Error loading label types:', error);
    } finally {
      setLoadingLabelTypes(false);
    }
  };

  const loadReceivingLocations = async () => {
    if (!companyNo || !warehouseID) return;

    setLoadingReceivingLocations(true);
    try {
      const locations = await stcBinService.getReceivingLocations(companyNo, warehouseID);
      setReceivingLocations(locations);

      // Set default if we have locations and a default is specified
      if (defaultReceivingLocation && locations.some(loc => loc.binCode === defaultReceivingLocation)) {
        setSelectedReceivingLocation(defaultReceivingLocation);
      } else if (locations.length > 0 && !selectedReceivingLocation) {
        // Auto-select first location if no default
        setSelectedReceivingLocation(locations[0].binCode);
      }
    } catch (error) {
      console.error('Error loading receiving locations:', error);
    } finally {
      setLoadingReceivingLocations(false);
    }
  };

  const loadItemDefaultReport = async () => {
    if (!companyNo || !line.itemNo) return;

    try {
      const reportId = await printingService.getItemDefaultReport(companyNo, line.itemNo);
      if (reportId && reportId > 0) {
        setSelectedLabelType(reportId);
      }
    } catch (error) {
      console.error('Error loading item default report:', error);
    }
  };

  const handlePrintLabel = async () => {
    if (!companyNo || !selectedLabelType || numOfLabels <= 0) {
      console.warn('Cannot print: missing companyNo, selectedLabelType, or invalid numOfLabels');
      return;
    }

    try {
      const printRequest = {
        reportID: selectedLabelType,
        numOfLabels: numOfLabels,
        itemNo: line.itemNo,
        itemUnitCode: line.itemUnitTxt || null,
        batchNo: batchNo || null,
        expiryDate: expDate || null,
        quantity: quantity || null
      };

      console.log('Printing label with request:', printRequest);

      const result = await printingService.printLabel(companyNo, printRequest);

      if (result.success) {
        // Show success notification (you can add a toast library later)
        console.log('✓ Print successful:', result.message);
        alert(`✓ Utskrift vellykket!\n${result.message || `${result.labelsProcessed || numOfLabels} etikett(er) sendt til skriver.`}`);
      } else {
        console.error('Print failed:', result.message);
        alert(`✗ Utskrift feilet:\n${result.message}`);
      }
    } catch (error: any) {
      console.error('Error printing label:', error);
      alert(`✗ Feil ved utskrift:\n${error.message || 'Ukjent feil'}`);
    }
  };

  /**
   * Handle SSCC/STAND005 pallet label printing
   */
  const handlePrintSSCCLabel = async () => {
    if (!companyNo || !enableSSCCPrinting) {
      console.warn('Cannot print SSCC: missing companyNo or SSCC printing not enabled');
      return;
    }

    setSSCCPrinting(true);
    try {
      // Generate SSCC using company prefix and incrementing counter
      const prefix = companyGS1Prefix || '7030000'; // Default Norwegian GS1 prefix
      const sscc = printingService.generateSSCC(prefix, ssccCounter);

      const ssccRequest = {
        sscc: sscc,
        gtin: itemGTIN || null,
        batchNo: batchNo || null,
        expiryDate: expDate || null,
        quantity: quantity ? Math.floor(quantity) : null,
        itemNo: line.itemNo,
        itemDescription: line.description1,
        numOfCopies: numOfLabels
      };

      console.log('Printing SSCC label with request:', ssccRequest);

      const result = await printingService.printSSCCLabelLegacy(companyNo, ssccRequest);

      if (result.success) {
        // Increment and persist SSCC counter
        const newCounter = ssccCounter + 1;
        setSSCCCounter(newCounter);
        localStorage.setItem('ssccCounter', newCounter.toString());

        console.log('✓ SSCC Print successful:', result.message);
        alert(`✓ SSCC-etikett vellykket!\nSSCC: ${sscc}\n${result.message}`);
      } else {
        console.error('SSCC Print failed:', result.message);
        alert(`✗ SSCC-utskrift feilet:\n${result.message}`);
      }
    } catch (error: any) {
      console.error('Error printing SSCC label:', error);
      alert(`✗ Feil ved SSCC-utskrift:\n${error.message || 'Ukjent feil'}`);
    } finally {
      setSSCCPrinting(false);
    }
  };

  const handleOpenSaldoModal = async () => {
    setShowSaldoModal(true);
    setLoadingSaldoLocations(true);
    try {
      const locations = await warehouseTransferService.getLocationsForItem(line.itemNo);
      setSaldoLocations(locations);
    } catch (error) {
      console.error('Error loading locations:', error);
      setSaldoLocations([]);
    } finally {
      setLoadingSaldoLocations(false);
    }
  };

  const handleCloseSaldoModal = () => {
    setShowSaldoModal(false);
    setSaldoLocations([]);
  };

  const handleQuantityChange = (value: number) => {
    console.log('handleQuantityChange called with:', value);
    // When editing existing StcTrans, don't limit to remaining quantity
    const validatedValue = Math.max(0, value);
    console.log('Setting quantity to:', validatedValue);
    setQuantity(validatedValue);
    if (onQuantityChange) {
      onQuantityChange(validatedValue);
    }
  };

  const handleBatchNoChange = (value: string) => {
    setBatchNo(value);
    if (onBatchInfoChange) {
      onBatchInfoChange(value, expDate);
    }
  };

  const handleExpDateChange = (value: string) => {
    setExpDate(value);
    if (onBatchInfoChange) {
      onBatchInfoChange(batchNo, value);
    }
  };

  const handleSave = () => {
    if (onSave) {
      // Always pass batch info and receiving location (both when editing and creating new)
      onSave(quantity, batchNo || undefined, expDate || undefined, selectedReceivingLocation || undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only save if user has explicitly interacted with quantity field
      // This prevents auto-save when scanner sends Enter after barcode
      if (isQuantityTouched || isEditingMode) {
        handleSave();
      } else {
        console.log('Enter ignored - quantity not touched by user yet');
      }
    } else if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="bg-white border-2 border-blue-300 rounded-lg shadow-md overflow-hidden mb-3 sm:mb-4">
      {/* Header with Title */}
      <div className="bg-blue-500 text-white px-3 sm:px-4 py-2 sm:py-3 flex items-center gap-2">
        <Package size={18} className="sm:hidden" />
        <Package size={20} className="hidden sm:block" />
        <h3 className="text-sm sm:text-base font-semibold">
          {isEditingMode ? 'Rediger eksisterende mottak' : 'Rediger mottak'}
        </h3>
      </div>

      {/* Action Buttons Row with Label Settings */}
      <div className="bg-gray-50 px-2 sm:px-3 py-2 flex items-center gap-2 flex-nowrap border-b border-gray-200 overflow-x-auto">
        {/* Grunndata button */}
        {onOpenGrunndata && (
          <button
            onClick={onOpenGrunndata}
            className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors font-medium"
            title="Vedlikehold grunndata for valgt vare"
          >
            <Database size={18} />
          </button>
        )}

        {/* Varesaldo button */}
        <button
          onClick={handleOpenSaldoModal}
          className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors font-medium"
          title="Vis varesaldo"
        >
          <Warehouse size={18} />
        </button>

        {/* Avslutt sorteringskasse button */}
        <button
          className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors font-medium"
          title="Avslutt sorteringskasse"
        >
          <PackageX size={18} />
        </button>

        {/* Print button - item label */}
        <button
          onClick={handlePrintLabel}
          disabled={!selectedLabelType || numOfLabels <= 0}
          className={`flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 rounded transition-colors font-medium ${
            selectedLabelType && numOfLabels > 0
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title="Skriv ut vareetikett"
        >
          <Printer size={18} />
        </button>

        {/* SSCC/STAND005 print button - only shown when enabled */}
        {enableSSCCPrinting && (
          <button
            onClick={handlePrintSSCCLabel}
            disabled={ssccPrinting || numOfLabels <= 0}
            className={`flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] sm:px-3 rounded transition-colors font-medium ${
              !ssccPrinting && numOfLabels > 0
                ? 'bg-purple-500 text-white hover:bg-purple-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title="Skriv ut SSCC/pall-etikett (GS1-128)"
          >
            {ssccPrinting ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <Tag size={18} />
            )}
          </button>
        )}

        {/* Separator */}
        <div className="flex-shrink-0 w-px h-6 bg-gray-300 mx-1"></div>

        {/* Number of labels */}
        <div className="flex-shrink-0 flex items-center gap-1">
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Antall:</label>
          <input
            type="number"
            min="1"
            value={numOfLabels}
            onChange={(e) => setNumOfLabels(parseInt(e.target.value) || 1)}
            className="w-12 px-1 py-1 border border-gray-300 rounded text-sm text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Label type dropdown */}
        <div className="flex-shrink-0 flex items-center gap-1 min-w-0">
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Etikett:</label>
          <select
            value={selectedLabelType || ''}
            onChange={(e) => setSelectedLabelType(e.target.value ? parseInt(e.target.value) : null)}
            disabled={loadingLabelTypes}
            className="w-32 px-1 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {loadingLabelTypes ? (
              <option value="">Laster...</option>
            ) : (
              <>
                <option value="">Velg...</option>
                {labelTypes.map((type) => (
                  <option key={type.reportID} value={type.reportID}>
                    {type.description}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>

        {/* Separator before receiving location */}
        <div className="flex-shrink-0 w-px h-6 bg-gray-300 mx-1"></div>

        {/* Receiving location dropdown */}
        <div className="flex-shrink-0 flex items-center gap-1 min-w-0">
          <MapPin size={16} className="text-gray-500 flex-shrink-0" />
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Ankomstlokasjon:</label>
          <select
            value={selectedReceivingLocation}
            onChange={(e) => setSelectedReceivingLocation(e.target.value)}
            disabled={loadingReceivingLocations}
            className="w-28 px-1 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {loadingReceivingLocations ? (
              <option value="">Laster...</option>
            ) : receivingLocations.length === 0 ? (
              <option value="">Ingen lokasjoner</option>
            ) : (
              <>
                {receivingLocations.map((loc) => (
                  <option key={loc.binCode} value={loc.binCode}>
                    {loc.binCode}
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-2 sm:p-3">
        {/* Item Info Section - Compact single line */}
        <div className="mb-2">
          <div className="text-sm text-gray-700">
            <span className="font-semibold">Varenr:</span> {line.itemNo} - {line.description1}
          </div>
        </div>

        {/* Four Column Stats - Compressed */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {/* Bestilt */}
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">Bestilt</div>
            <div className="text-base sm:text-lg font-bold text-gray-900">
              {line.quantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Mottatt */}
          <div className="bg-green-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">Mottatt</div>
            <div className="text-base sm:text-lg font-bold text-green-700">
              {line.totalReceived} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Ankommet */}
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">Ankommet</div>
            <div className="text-base sm:text-lg font-bold text-blue-700">
              {line.arrivedQuantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>

          {/* Gjenstår */}
          <div className="bg-orange-50 p-2 rounded">
            <div className="text-xs font-medium text-gray-500 mb-0.5 sm:mb-1">Gjenstår</div>
            <div className={`text-base sm:text-lg font-bold ${
              line.remainingQuantity === 0 ? 'text-green-700' : 'text-orange-600'
            }`}>
              {line.remainingQuantity} <span className="text-xs font-normal text-gray-600">{line.itemUnitTxt}</span>
            </div>
          </div>
        </div>

        {/* Batch Info - Single line with label + input for both */}
        {(isEditingMode || line.itemType === 1 || line.itemType === 4 || scannedBatchInfo?.batchNo || scannedBatchInfo?.expDate) && (
          <div className="flex items-center gap-2 mb-2 flex-nowrap">
            {/* Batch Number */}
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap flex-shrink-0">
              Batchnr
              {((isEditingMode && (editingStcTrans?.itemType === 1 || editingStcTrans?.itemType === 4)) ||
                (!isEditingMode && (line.itemType === 1 || line.itemType === 4))) && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <input
              type="text"
              value={batchNo}
              onChange={(e) => handleBatchNoChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`flex-1 min-w-0 px-2 py-1.5 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                ((isEditingMode && (editingStcTrans?.itemType === 1 || editingStcTrans?.itemType === 4) && !batchNo) ||
                 (!isEditingMode && (line.itemType === 1 || line.itemType === 4) && !batchNo))
                  ? 'border-red-300 bg-red-50'
                  : scannedBatchInfo?.batchNo && !isEditingMode
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
              placeholder="Batchnummer"
            />

            {/* Expiry Date */}
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap flex-shrink-0">
              Utløp
              {((isEditingMode && editingStcTrans?.itemType === 4) ||
                (!isEditingMode && line.itemType === 4)) && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <input
              type="date"
              value={expDate}
              onChange={(e) => handleExpDateChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-32 flex-shrink-0 px-2 py-1.5 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                ((isEditingMode && editingStcTrans?.itemType === 4 && !expDate) ||
                 (!isEditingMode && line.itemType === 4 && !expDate))
                  ? 'border-red-300 bg-red-50'
                  : scannedBatchInfo?.expDate && !isEditingMode
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300'
              }`}
            />
          </div>
        )}

        {/* Validation Messages */}
        {validationError && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 mb-4 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-700">{validationError}</p>
            </div>
          </div>
        )}

        {validationWarning && !validationError && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-4 rounded">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-yellow-700">{validationWarning}</p>
            </div>
          </div>
        )}

        {/* Quantity Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Antall å motta nå
          </label>
          <div className="flex items-center gap-2">
            <input
              ref={quantityInputRef}
              type="number"
              inputMode="decimal"
              step="0.1"
              value={quantity}
              onChange={(e) => {
                setInputJustFocused(false);
                setIsQuantityTouched(true); // Mark that user has interacted with quantity
                const numValue = parseFloat(e.target.value);
                handleQuantityChange(isNaN(numValue) ? 0 : numValue);
              }}
              onMouseDown={() => setInputJustFocused(true)}
              onFocus={(e) => {
                if (inputJustFocused) {
                  e.target.select();
                }
              }}
              onBlur={() => setInputJustFocused(false)}
              onKeyDown={handleKeyDown}
              className="flex-1 px-4 py-3 text-xl font-bold border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
            <span className="text-base font-medium text-gray-600 min-w-[40px]">
              {line.itemUnitTxt}
            </span>
          </div>
        </div>

        {/* Action Buttons - All on one line */}
        <div className="flex items-center gap-2">
          {/* Lagre - with text */}
          <button
            onClick={handleSave}
            disabled={quantity <= 0}
            className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded font-medium transition-colors ${
              quantity > 0
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={18} />
            <span className="text-sm sm:text-base">Lagre</span>
          </button>

          {/* Direktemottak - with text */}
          {onDirectReceipt && (
            <button
              onClick={() => onDirectReceipt(quantity, batchNo || undefined, expDate || undefined, selectedReceivingLocation || undefined)}
              disabled={quantity <= 0}
              className={`flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 rounded font-medium transition-colors ${
                quantity > 0
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="Direktemottak"
            >
              <ArrowRight size={18} />
              <span className="text-sm sm:text-base">Direkte</span>
            </button>
          )}

          {/* Slett - icon only */}
          {isEditingMode && onDelete && (
            <button
              onClick={onDelete}
              className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              title="Slett"
            >
              <Trash2 size={18} />
            </button>
          )}

          {/* Avbryt - icon only */}
          {onCancel && (
            <button
              onClick={onCancel}
              className="flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              title="Avbryt"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Saldo Modal */}
      <LocationSelectionModal
        isOpen={showSaldoModal}
        onClose={handleCloseSaldoModal}
        onSelect={() => {}} // Dummy function - vil ikke bli kalt i viewMode
        itemNo={line.itemNo}
        itemDescription={line.description1}
        locations={saldoLocations}
        loading={loadingSaldoLocations}
        viewMode={true}
      />
    </div>
  );
};
